import { useId, type ReactNode } from 'react';

type PrimaryVariant = 'default' | 'positive' | 'onaccent';

interface PrimaryButtonProps {
  children: ReactNode;
  onClick?: () => void;
  /** Uitgeschakeld blijft focusbaar en draagt altijd een zichtbare reden (toegankelijkheid). */
  disabledReason?: string | null;
  variant?: PrimaryVariant;
  type?: 'button' | 'submit';
  autoFocus?: boolean;
  /** Koppelt de knop aan een formulier dat elders in de DOM staat (de voet ligt buiten de body). */
  form?: string;
}

/** Primaire knop: 64 px, cream, één per scherm (README §Componenten). */
export function PrimaryButton({
  children,
  onClick,
  disabledReason = null,
  variant = 'default',
  type = 'button',
  autoFocus = false,
  form,
}: PrimaryButtonProps) {
  const reasonId = useId();
  const disabled = disabledReason !== null;
  const variantClass =
    variant === 'positive'
      ? ' btn-primary--positive'
      : variant === 'onaccent'
        ? ' btn-primary--onaccent'
        : '';

  return (
    <>
      <button
        type={type}
        form={form}
        className={`btn btn-primary${variantClass}`}
        aria-disabled={disabled || undefined}
        aria-describedby={disabled ? reasonId : undefined}
        autoFocus={autoFocus}
        onClick={() => {
          if (!disabled) onClick?.();
        }}
      >
        {children}
      </button>
      {disabled ? (
        <p className="btn-reason" id={reasonId}>
          {disabledReason}
        </p>
      ) : null}
    </>
  );
}

interface SecondaryButtonProps {
  children: ReactNode;
  onClick?: () => void;
  disabledReason?: string | null;
  variant?: 'default' | 'scanner' | 'onaccent';
}

/** Secundaire knop: 56 px, transparant met rand. */
export function SecondaryButton({
  children,
  onClick,
  disabledReason = null,
  variant = 'default',
}: SecondaryButtonProps) {
  const reasonId = useId();
  const disabled = disabledReason !== null;
  const variantClass =
    variant === 'scanner'
      ? ' btn-secondary--scanner'
      : variant === 'onaccent'
        ? ' btn-secondary--onaccent'
        : '';

  return (
    <>
      <button
        type="button"
        className={`btn btn-secondary${variantClass}`}
        aria-disabled={disabled || undefined}
        aria-describedby={disabled ? reasonId : undefined}
        onClick={() => {
          if (!disabled) onClick?.();
        }}
      >
        {children}
      </button>
      {disabled ? (
        <p className="btn-reason" id={reasonId}>
          {disabledReason}
        </p>
      ) : null}
    </>
  );
}

interface TextButtonProps {
  children: ReactNode;
  onClick?: () => void;
  variant?: 'mint' | 'muted' | 'onaccent';
  center?: boolean;
  ariaLabel?: string;
}

/** Tekstknop; raakvlak blijft ≥ 44 px door de padding in `components.css`. */
export function TextButton({
  children,
  onClick,
  variant = 'mint',
  center = false,
  ariaLabel,
}: TextButtonProps) {
  const variantClass =
    variant === 'muted'
      ? ' btn-text--muted'
      : variant === 'onaccent'
        ? ' btn-text--onaccent'
        : '';
  return (
    <button
      type="button"
      aria-label={ariaLabel}
      className={`btn-text${variantClass}${center ? ' btn-text--center' : ''}`}
      onClick={onClick}
    >
      {children}
    </button>
  );
}
